/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import {
  View,
  TouchableOpacity,
  LayoutChangeEvent,
  Platform,
  Keyboard,
} from 'react-native';
import {
  pd,
  getColor,
  textStyle,
} from '@airasia-phoenix/react-native/utils/utils';

import IconView from '@airasia-phoenix/react-native/components/IconView';
import SpinnerLoaderView from '@airasia-phoenix/react-native/components/SpinnerLoaderView';
import {
  SpinnerLoaderViewSize,
  SpinnerLoaderViewStyle,
} from '@airasia-phoenix/react-native/components/SpinnerLoaderView/types';
import { useLogger } from '@airasia-rn/core-logger';
import BottomSheetView, {
  BottomSheetRefs,
} from '@airasia-phoenix/react-native/components/BottomSheetView';
import { useBottomSheetBackdropWebFix } from '../../hooks/useBottomSheetBackdropWebFix';
import SelectionFieldView, {
  SelectionFieldRefs,
} from '@airasia-phoenix/react-native/components/SelectionFieldView';
import ChipGroupView from '@airasia-phoenix/react-native/components/ChipGroupView';
import SearchableListPageView, {
  ViewType,
  SectionListItem,
} from '@airasia-phoenix/react-native/components/SearchableListPageView';
import { Alignment } from '@airasia-phoenix/react-native/components/SearchableListPageView/ListEmptyComponent';
import { MemoizedListViewProps } from '@airasia-phoenix/react-native/components/SearchableListPageView/MemoizedListView';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import {
  autoCapitalizeValues,
  TextFieldRefs,
} from '@airasia-phoenix/react-native/components/TextFieldView/types';
import { useDeviceInfo } from '@airasia-rn/ui';
import { useSetting } from '@airasia-rn/account/hooks';
import { SearchWidgetEvent, SearchWidgetMeta } from '../../analytics';
import SkeletonLoaderView from '@airasia-phoenix/react-native/components/SkeletonLoaderView';
import { useTranslation } from 'react-i18next';
import type { Suggestions, SearchStationsViewProps } from '../../types';
import { useRecentSearches } from '../../hooks/useRecentSearch';
import {
  useSmartFillSuggestions,
  usePopularSearches,
} from '../../hooks/hotels';
import { useIsClientHydrated } from '../../hooks/useIsClientHydrated';
import { useSearchViewFeatureFlag } from '../../hooks/useSearchViewFeatureFlag';
import { SMARTFILL_LAST_RECENT_SEARCHES_CT } from '../../configs/settingsConstant';
import ERROR_FIELD from '../../error';
import {
  DROP_DOWN_HEIGHT,
  DROP_DOWN_PORTAL_HOST_NAME,
  DROP_DOWN_WIDTH,
  DROP_DOWN_ITEM_HEIGHT,
  DROP_DOWN_HEADER_HEIGHT,
  MAX_RECENT_SEARCHES,
  MIN_QUERY_LENGTH,
  HOTEL,
} from '../../configs/constants';
import {
  useStationListData,
  normalizeHotelSectionData,
} from '../../utils/stationSearchListUtils';
import { popularSearchesOrientation } from '../../utils';
import { HotelDestinationListItem, HotelSection } from '../../types';
import { DESTINATION_SHEET_HEIGHT, styles } from './styles';
import { PopularSearchesSection } from './PopularSearchesSection';
import { useListFeatureFlag } from '../../hooks';

const DestinationSearchView: React.FC<SearchStationsViewProps> = ({
  onSearch,
  onStationSelect,
  initialStation = null,
  isLoading = false,
  testID = 'hotel-search-widget',
  destinationErrorField,
  updateDestinationErrorField,
}) => {
  const { t } = useTranslation('hotels');
  const { t: t1 } = useTranslation('snap');
  const { logEvent } = useLogger();

  const destinationFieldRef = useRef<SelectionFieldRefs>(null);
  const destinationSheetRef = useRef<BottomSheetRefs>(null);
  const destinationMobileInputRef = useRef<TextFieldRefs>(null);
  const destinationFocusTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const isDestinationSheetOpenRef = useRef(false);
  const shouldFocusDestinationSheetRef = useRef(false);
  const lastTypedValueRef = useRef<string>(''); // Store last typed value for analytics

  const [keyboardHeight, setKeyboardHeight] = useState(0);

  const { isWeb, isMobileWidth } = useDeviceInfo();
  const isMobileWeb = isWeb && isMobileWidth;
  const isDesktopWeb = isWeb && !isMobileWidth;

  useEffect(() => {
    if (isDesktopWeb) return;

    const showEvent =
      Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow';
    const hideEvent =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';

    const onShow = (e: any) => {
      setKeyboardHeight(e.endCoordinates.height);
    };
    const onHide = () => {
      setKeyboardHeight(0);
    };

    const showSub = Keyboard.addListener(showEvent, onShow);
    const hideSub = Keyboard.addListener(hideEvent, onHide);

    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [isDesktopWeb]);

  const [containerWidth, setContainerWidth] = useState<number>(DROP_DOWN_WIDTH);

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      const { width } = event.nativeEvent.layout;
      if (isDesktopWeb) {
        setContainerWidth(width);
      }
    },
    [isDesktopWeb],
  );

  const [destinationStation, setDestinationStation] =
    useState<Suggestions | null>(initialStation);
  const [selectionFieldKey, setSelectionFieldKey] = useState(0);
  const previousInitialStationRef = useRef(initialStation);

  const initialStationId = initialStation?.id ?? initialStation?.aa_id ?? '';
  const previousStationId =
    previousInitialStationRef.current?.id ??
    previousInitialStationRef.current?.aa_id ??
    '';

  if (
    initialStationId !== previousStationId ||
    initialStation?.name !== previousInitialStationRef.current?.name
  ) {
    setDestinationStation(initialStation);
    previousInitialStationRef.current = initialStation;
  }

  const { hotel_popular_searches_enabled } = useSearchViewFeatureFlag();
  const { data: popularSearchesData } = usePopularSearches(
    hotel_popular_searches_enabled === true,
  );
  const popularSearches = popularSearchesData || [];

  const { addRecentSearch, recentSearches: rawRecentSearches } =
    useRecentSearches();
  const isClientHydrated = useIsClientHydrated();
  const recentSearches = isClientHydrated ? rawRecentSearches : [];
  const { value: recentSearchCountSetting } = useSetting(
    SMARTFILL_LAST_RECENT_SEARCHES_CT,
  );
  const parsedRecentSearchCount = recentSearchCountSetting
    ? parseInt(recentSearchCountSetting, 10)
    : NaN;
  const recentSearchCount = Number.isNaN(parsedRecentSearchCount)
    ? MAX_RECENT_SEARCHES
    : parsedRecentSearchCount;

  const [destinationQuery, setDestinationQuery] = useState('');
  // State for API calls
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const [errors, setErrors] = useState<{ destination?: string }>({});

  const [isDestinationSheetOpen, setIsDestinationSheetOpen] = useState(false);
  const [isDestinationFocused, setIsDestinationFocused] = useState(false);
  useBottomSheetBackdropWebFix(isDestinationSheetOpen);

  const clearDestinationFocusTimeout = useCallback(() => {
    if (destinationFocusTimeoutRef.current) {
      clearTimeout(destinationFocusTimeoutRef.current);
      destinationFocusTimeoutRef.current = null;
    }
  }, []);

  // Ref to track if we're setting query from selection (to prevent API call)
  const isSelectingRef = useRef(false);

  const hasLoggedPopularSearchesRef = useRef(false);

  useEffect(() => {
    const isVisible = isDesktopWeb
      ? isDestinationFocused
      : isDestinationSheetOpen;
    if (
      isVisible &&
      popularSearches.length > 0 &&
      destinationQuery === '' &&
      !hasLoggedPopularSearchesRef.current
    ) {
      const items = popularSearches.map((suggest, index) => ({
        item_id: suggest.id ?? suggest.aa_id ?? '',
        item_name: suggest.name ?? '',
        item_category: suggest.type ?? 'city',
        index: index + 1,
      }));
      const params = SearchWidgetEvent.popularSearchViewedEvent(items);
      logEvent('view_item_list', params);
      hasLoggedPopularSearchesRef.current = true;
    }

    if (!isVisible) {
      hasLoggedPopularSearchesRef.current = false;
    }
  }, [
    isDesktopWeb,
    isDestinationFocused,
    isDestinationSheetOpen,
    popularSearches,
    destinationQuery,
    logEvent,
  ]);

  // Update error message when error field changes
  useEffect(() => {
    const nextDestinationError =
      destinationErrorField === ERROR_FIELD.DESTINATION_FIELD
        ? t('searchHotelPage.destinationSearch.error')
        : undefined;

    setErrors((previousErrors) => {
      if (previousErrors.destination === nextDestinationError) {
        return previousErrors;
      }

      return nextDestinationError ? { destination: nextDestinationError } : {};
    });
  }, [destinationErrorField, t]);

  // Capture user input as they type for analytics
  useEffect(() => {
    if (destinationQuery && !isSelectingDestination.current) {
      lastTypedValueRef.current = destinationQuery;
    } else if (!destinationQuery) {
      lastTypedValueRef.current = '';
    }
  }, [destinationQuery]);

  useEffect(() => {
    if (!destinationQuery || isSelectingDestination.current) return;

    const trackingTimeout = setTimeout(() => {
      const safeInput = destinationQuery?.trim();
      if (
        !safeInput ||
        safeInput === 'undefined' ||
        safeInput === 'undefined (undefined)'
      ) {
        return;
      }

      // Fire unified smart fill analytics event
      const params = SearchWidgetEvent.smartFillInputEvent(
        safeInput,
        SearchWidgetMeta.PROVIDER.TYPESENSE, // TODO: Determine how to identify provider (TypeSense vs ElasticSearch)
      );
      logEvent(SearchWidgetMeta.EVENT_NAMES.SMART_FILL_INPUT, params);
    }, 2000);

    return () => {
      clearTimeout(trackingTimeout);
    };
  }, [destinationQuery, logEvent]);

  const recentDestinationSearches = useMemo(() => {
    return recentSearches.slice(0, recentSearchCount).map((search) => ({
      station: search.destination,
      isAllAirports: search.destination.type === HOTEL,
    }));
  }, [recentSearches, recentSearchCount]);

  useEffect(() => {
    // Don't debounce if we just selected an item
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedQuery(destinationQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [destinationQuery]);

  // Hook triggered only when debouncedQuery changes
  const {
    data,
    isLoading: isLoadingStations,
    error,
  } = useSmartFillSuggestions({ query: debouncedQuery });

  const lastTrackedQuery = useRef<string | null>(null);
  const isSelectingDestination = useRef(false);
  const isAboveMinLength = destinationQuery.trim().length >= MIN_QUERY_LENGTH;

  useEffect(() => {
    if (
      data?.suggestions &&
      (data?.suggestions?.length ?? 0) <= 0 &&
      lastTrackedQuery.current !== debouncedQuery
    ) {
      lastTrackedQuery.current = debouncedQuery;

      const safeInput = debouncedQuery.trim();

      // Fire unified no results analytics event
      const params = SearchWidgetEvent.noResultsEvent(safeInput);
      logEvent('tap', params);
    }
  }, [error, data, isLoadingStations, debouncedQuery, logEvent]);

  const stations: Suggestions[] = useMemo(() => {
    const trimmedQuery = destinationQuery.trim();

    if (trimmedQuery.length < MIN_QUERY_LENGTH) {
      return [];
    }

    if (trimmedQuery !== debouncedQuery.trim()) {
      return [];
    }

    return data?.suggestions ?? [];
  }, [destinationQuery, debouncedQuery, data]);

  const destinationListData = useStationListData({
    query: destinationQuery,
    stations,
    recentstations: recentDestinationSearches,
    t,
  });

  const normalizedDestinationListData = normalizeHotelSectionData(
    destinationListData as HotelSection<HotelDestinationListItem>[],
  );

  const isBelowMinLength =
    destinationQuery.length > 0 && destinationQuery.length < MIN_QUERY_LENGTH;
  const isSearching =
    isBelowMinLength ||
    isLoadingStations ||
    destinationQuery !== debouncedQuery;
  const hasApiSettled =
    isAboveMinLength &&
    destinationQuery === debouncedQuery &&
    !isLoadingStations;

  const totalItems = normalizedDestinationListData.reduce(
    (acc, itemOrSection: any) => {
      if (itemOrSection.data) {
        return acc + (itemOrSection.data.length || 0);
      }
      return acc + 1;
    },
    0,
  );

  const dynamicHeight =
    destinationQuery === ''
      ? undefined
      : totalItems > 0
        ? Math.min(
            DROP_DOWN_HEIGHT,
            totalItems * DROP_DOWN_ITEM_HEIGHT + DROP_DOWN_HEADER_HEIGHT + 24, // 24px for padding (12px top + 12px bottom)
          )
        : DROP_DOWN_HEIGHT;

  const handleDestinationSelect = useCallback(
    (selectedItems?: MemoizedListViewProps[]) => {
      const selected = selectedItems?.[0];

      if (!selected || !selected.model) {
        return;
      }
      const model = selected.model as any;
      const isPopular = model?.trackingSource === 'popular_searches';

      const station = model.place ?? model.station;

      if (station) {
        const position = model.trackingIndex ?? 1;
        if (isPopular) {
          const params = SearchWidgetEvent.popularSearchClickEvent(
            station.name,
            position,
            null,
            station.type ?? '',
            station.id ?? station.aa_id ?? '',
          );
          logEvent('tap', params);
        } else {
          const params = SearchWidgetEvent.suggestionClickEvent(
            station.name,
            position,
          );
          logEvent('tap', params);
        }
        // Set flag to prevent smartfill input event from firing
        if (!isPopular) {
          isSelectingDestination.current = true;
        }

        setDestinationStation(station);
        void addRecentSearch(station);
        onSearch?.({
          to: station,
        });
        onStationSelect?.(station);
        updateDestinationErrorField?.(undefined);
        // Set flag to prevent debounce from triggering API call
        isSelectingRef.current = true;

        if (isPopular) {
          setSelectionFieldKey((prev) => prev + 1);
        }

        queueMicrotask(() => {
          setDestinationQuery('');
          setDebouncedQuery('');
          setErrors({});
        });

        // Reset flag after a short delay to allow for normal input tracking
        setTimeout(() => {
          isSelectingDestination.current = false;
        }, 100);
      }

      if (isDesktopWeb) {
        setIsDestinationFocused(false);
        if (isPopular) {
          destinationFieldRef.current?.blurWithText();
          destinationFieldRef.current?.blur();
          if (typeof document !== 'undefined') {
            document.dispatchEvent(
              new KeyboardEvent('keydown', {
                key: 'Escape',
                keyCode: 27,
                bubbles: true,
              }),
            );
            if (document.activeElement) {
              (document.activeElement as HTMLElement).blur();
            }
          }
        } else {
          destinationFieldRef.current?.focus();
        }
        return;
      }

      clearDestinationFocusTimeout();
      shouldFocusDestinationSheetRef.current = false;
      destinationSheetRef.current?.toggleBottomSheetView();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- addRecentSearch is excluded to prevent infinite loop
    [
      isDesktopWeb,
      onSearch,
      onStationSelect,
      updateDestinationErrorField,
      logEvent,
      clearDestinationFocusTimeout,
    ],
  );

  const resolvedNormalizedDestinationListData = useMemo(() => {
    if (destinationQuery !== '' || popularSearches.length === 0) {
      return normalizedDestinationListData;
    }
    return [
      ...normalizedDestinationListData,
      {
        title: t('searchHotelPage.popularSearches.title'),
        data: [popularSearches],
        renderItem: ({ item }: { item: Suggestions[] }) => (
          <PopularSearchesSection
            item={item}
            onSelect={handleDestinationSelect}
            styles={styles}
            orientation={popularSearchesOrientation}
          />
        ),
      },
    ] as SectionListItem[];
  }, [
    normalizedDestinationListData,
    destinationQuery,
    popularSearches,
    handleDestinationSelect,
    t,
    styles,
  ]);

  const resolvedDestinationListData = useMemo(() => {
    if (destinationQuery !== '' || popularSearches.length === 0) {
      return destinationListData;
    }
    return [
      ...(destinationListData as SectionListItem[]),
      {
        title: t('searchHotelPage.popularSearches.title'),
        data: [popularSearches],
        renderItem: ({ item }: { item: Suggestions[] }) => (
          <PopularSearchesSection
            item={item}
            onSelect={handleDestinationSelect}
            styles={styles}
            orientation={popularSearchesOrientation}
          />
        ),
      },
    ] as SectionListItem[];
  }, [
    destinationListData,
    destinationQuery,
    popularSearches,
    handleDestinationSelect,
    t,
    styles,
  ]);

  const openDestinationSheet = useCallback(() => {
    if (isDestinationSheetOpenRef.current) {
      return;
    }

    isDestinationSheetOpenRef.current = true;
    setIsDestinationSheetOpen(true);
    shouldFocusDestinationSheetRef.current = isMobileWeb;
    const toggleDestinationSheet = () => {
      destinationSheetRef.current?.toggleBottomSheetView();
    };

    if (isMobileWeb) {
      requestAnimationFrame(toggleDestinationSheet);
      return;
    }

    toggleDestinationSheet();
  }, [isMobileWeb]);

  const resetDestinationSheet = useCallback(() => {
    isDestinationSheetOpenRef.current = false;
    setIsDestinationSheetOpen(false);
    shouldFocusDestinationSheetRef.current = false;
    clearDestinationFocusTimeout();
    setDestinationQuery('');
    setDebouncedQuery('');
  }, [clearDestinationFocusTimeout]);

  const handleDestinationSheetChange = useCallback(
    (index: number) => {
      if (
        !isMobileWeb ||
        index < 0 ||
        !shouldFocusDestinationSheetRef.current
      ) {
        return;
      }

      shouldFocusDestinationSheetRef.current = false;
      clearDestinationFocusTimeout();
      destinationFocusTimeoutRef.current = setTimeout(() => {
        destinationMobileInputRef.current?.focus();
      }, 150);
    },
    [clearDestinationFocusTimeout, isMobileWeb],
  );

  const { hotel_enable_portal_usage: isPortalUsageEnabled } =
    useListFeatureFlag();

  if (isLoading || (isLoadingStations && destinationQuery === '')) {
    return (
      <View style={styles.rowDouble} testID="snap-widget-skeleton">
        <View style={styles.halfWidth}>
          <SkeletonLoaderView style={styles.skeletonHalf} />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container} testID={testID}>
      <View style={styles.stationRow}>
        <View
          testID={`${testID}-field-wrapper`}
          style={styles.fieldWrapper}
          onLayout={handleLayout}
          onStartShouldSetResponderCapture={() => {
            if (isDesktopWeb) {
              setIsDestinationFocused(true);
            }
            return false;
          }}
        >
          {isDesktopWeb ? (
            <SelectionFieldView
              key={`sfv-${selectionFieldKey}`}
              ref={destinationFieldRef}
              disabled={false}
              isError={!!errors.destination}
              errorMessage={errors.destination}
              endIcon={undefined}
              label={t('searchHotelPage.destinationSearch.title')}
              value={destinationStation?.name ?? ''}
              width="100%"
              testID={`${testID}-to`}
              searchableListViewProps={{
                viewType: isPortalUsageEnabled
                  ? ViewType.DropDownWithPortal
                  : ViewType.DropDown,
                dropDownHeight: dynamicHeight,
                dropDownWidth: containerWidth,
                dropdownPortalHostName: DROP_DOWN_PORTAL_HOST_NAME,
                isTextFieldVisible: false,
                dropDownZIndex: 24,
                showItemSeparatorComponent: false,
                isLoading: isLoadingStations && isAboveMinLength,
                loaderViewProps: {
                  numberOfLoaders: 1,
                  children: (
                    <View style={styles.spinnerContainer}>
                      <SpinnerLoaderView
                        color={getColor(pd.sys.colour.secondary)}
                        size={SpinnerLoaderViewSize.LARGE}
                        spinnerStyle={SpinnerLoaderViewStyle.SPINNER}
                      />
                    </View>
                  ),
                },
                paddingTop: isPortalUsageEnabled ? undefined : 54,
                startedSearching: hasApiSettled,
                listEmptyComponentProps: {
                  contentStateViewProps: {
                    primaryText: t1('searchSnapPage.noResultFound.title'),
                    secondaryText: t1('searchSnapPage.noResultFound.subtext'),
                  },
                  alignment: Alignment.TOP,
                },
                headerTextStyle: textStyle(
                  pd.sys.typescale.body_text_medium,
                  pd.sys.colour.content_primary,
                ),
                textFieldViewProps: {
                  autoCorrect: false,
                  disabled: false,
                  autoCapitalize: autoCapitalizeValues.NONE,
                  label: t('searchHotelPage.destinationSearch.title'),
                  endIcon: {
                    icon: pd.icon.main.navigation.cancel,
                    height: pd.ref.space.spacing60.px,
                    width: pd.ref.space.spacing60.px,
                    tint: getColor(pd.sys.colour.content_primary),
                  },
                  onEndIconPress: () => setDestinationQuery(''),
                  selectTextOnFocus: isDesktopWeb,
                },
                searchableListPageViewData:
                  resolvedNormalizedDestinationListData,
                onChangeText: setDestinationQuery,
                onChange: handleDestinationSelect,
              }}
              bottomSheetViewProps={{
                ...DESTINATION_SHEET_HEIGHT,
                children: undefined,
                showHandle: false,
                sectionTitle: '',
                onBottomSheetClose: () => {
                  destinationFieldRef.current?.blurWithText();
                  destinationFieldRef.current?.blur();
                  setDestinationQuery('');
                  setDebouncedQuery('');
                  setIsDestinationFocused(false);
                },
                onCloseIconPress: () => {
                  destinationFieldRef.current?.blurWithText();
                  destinationFieldRef.current?.blur();
                  setDestinationQuery('');
                  setDebouncedQuery('');
                  setIsDestinationFocused(false);
                },
              }}
            />
          ) : (
            <TextFieldView
              disabled={false}
              label={t('searchHotelPage.destinationSearch.title')}
              value={
                destinationStation?.name && destinationStation.name.length > 47
                  ? `${destinationStation.name.slice(0, 45)}...`
                  : (destinationStation?.name ?? '')
              }
              isError={!!errors.destination}
              errorMessage={errors.destination}
              width="100%"
              editable={false}
              onPress={openDestinationSheet}
              testID={`${testID}-to`}
            />
          )}
        </View>
      </View>

      {!isDesktopWeb && (
        <BottomSheetView
          ref={destinationSheetRef}
          enableContentPanningGesture={false}
          enablePanDownToClose={!isMobileWeb}
          enableOverDrag={false}
          showBackdrop
          backdropProps={{
            opacity: 1,
            backgroundColor: getColor(pd.sys.colour.overlay),
          }}
          showHandle={false}
          showHeader={false}
          sectionTitle=""
          {...DESTINATION_SHEET_HEIGHT}
          onChange={handleDestinationSheetChange}
          onBottomSheetClose={resetDestinationSheet}
        >
          <View style={styles.sheetHeaderRow}>
            <TouchableOpacity
              style={styles.sheetCloseButton}
              onPress={() => {
                clearDestinationFocusTimeout();
                shouldFocusDestinationSheetRef.current = false;
                destinationSheetRef.current?.toggleBottomSheetView();
                setDestinationQuery('');
                setDebouncedQuery('');
              }}
            >
              <IconView
                icon={pd.icon.main.navigation.close}
                height={pd.ref.space.spacing50.px}
                width={pd.ref.space.spacing50.px}
                tint={getColor(pd.sys.colour.content_primary)}
              />
            </TouchableOpacity>

            <View style={styles.sheetInputWrapper}>
              <TextFieldView
                ref={destinationMobileInputRef}
                disabled={false}
                label={t('searchHotelPage.destinationSearch.title')}
                value={destinationQuery}
                autoCorrect={false}
                autoFocus={!isMobileWeb}
                autoCapitalize={autoCapitalizeValues.NONE}
                onChangeText={setDestinationQuery}
              />
            </View>
          </View>

          <View
            style={[
              styles.sheetListContainer,
              {
                paddingBottom:
                  ((styles.sheetListContainer?.paddingBottom as number) ?? 0) +
                  keyboardHeight,
              },
            ]}
          >
            {isLoadingStations && isAboveMinLength ? (
              <View style={styles.spinnerContainer}>
                <SpinnerLoaderView
                  color={getColor(pd.sys.colour.secondary)}
                  size={SpinnerLoaderViewSize.LARGE}
                  spinnerStyle={SpinnerLoaderViewStyle.SPINNER}
                />
              </View>
            ) : (
              <SearchableListPageView
                key={`dest-list-${isAboveMinLength}-${isDestinationSheetOpen}`}
                searchableListPageViewData={resolvedDestinationListData}
                showItemSeparatorComponent={false}
                isLoading={false}
                startedSearching={
                  destinationQuery.length >= MIN_QUERY_LENGTH && !isSearching
                }
                listEmptyComponentProps={{
                  contentStateViewProps: {
                    primaryText: t1('searchSnapPage.noResultFound.title'),
                    secondaryText: t1('searchSnapPage.noResultFound.subtext'),
                  },
                  alignment: Alignment.TOP,
                  searchValue: destinationQuery,
                }}
                headerTextStyle={textStyle(
                  pd.sys.typescale.body_text_medium,
                  pd.sys.colour.content_primary,
                )}
                onChange={handleDestinationSelect}
                numColumns={1}
                isInsideBottomSheetView={isDesktopWeb}
                isTextFieldVisible={false}
              />
            )}
          </View>
        </BottomSheetView>
      )}
    </View>
  );
};

export default DestinationSearchView;
